// SW update acceptance — the E-10 (G1 audit) fix.
// vite-plugin-pwa runs in PROMPT mode (vite.config.ts registerType — the #248/#249
// lock: a new service worker installs and WAITS; the tab is never force-reloaded).
// The generated sw.js activates ONLY on a {type:'SKIP_WAITING'} message, and the
// ONLY sender is the callback registerSW() RETURNS (register.js → wb.messageSkipWaiting()).
// main.tsx used to discard that return, so the banner's bare reload could never
// deliver an update: the waiting worker stayed waiting, the old shell re-served from
// the old worker's precache, and the banner looped forever.
//
// Accept flow (verified against vite-plugin-pwa 1.3.0 + workbox-window 7.4.1 in
// node_modules): click → updateServiceWorker() → SKIP_WAITING → sw.js
// self.skipWaiting() → new worker takes over the old worker's clients →
// controllerchange → the PLUGIN'S own 'controlling' listener (attached when it fired
// onNeedRefresh) reloads the page → the navigation is served by the NEW worker's
// precache. The callback's reloadPage argument is ignored since v0.13.2 — do NOT add
// a controllerchange listener here; the plugin owns the normal reload.
//
// The fallback timer covers the two paths where that chain cannot complete:
//  • nothing is actually waiting (another tab already activated the update) —
//    messageSkipWaiting is a documented no-op → plain reload picks up whatever is
//    now active (never worse than the old behavior);
//  • this tab is UNCONTROLLED (hard-refresh session): without clientsClaim —
//    deliberately absent, part of the #248 lock — controllerchange never fires on an
//    uncontrolled client, so no plugin reload would ever come.
// Every reload in this module is strictly downstream of the user's click.

export const SW_UPDATE_EVENT = 'rizeos:sw-update-available'

export const SW_UPDATE_FALLBACK_MS = 2500

/** Structural type of the callback registerSW() (virtual:pwa-register) returns. */
type UpdateServiceWorkerFn = (reloadPage?: boolean) => Promise<void>

let updateServiceWorkerFn: UpdateServiceWorkerFn | null = null

/** main.tsx wires the captured registerSW return here at startup. */
export function wireSwUpdater(fn: UpdateServiceWorkerFn | null): void {
  updateServiceWorkerFn = fn
}

/** Injectable browser effects so the accept logic runs under node --test. */
export interface AcceptSwUpdateIo {
  reload: () => void
  setTimeout: (cb: () => void, ms: number) => number
  clearTimeout: (id: number) => void
}

function browserIo(): AcceptSwUpdateIo {
  return {
    reload: () => window.location.reload(),
    setTimeout: (cb, ms) => window.setTimeout(cb, ms),
    clearTimeout: (id) => window.clearTimeout(id),
  }
}

export async function acceptSwUpdate(io: AcceptSwUpdateIo = browserIo()): Promise<void> {
  const fn = updateServiceWorkerFn
  if (!fn) {
    // Defensive: nothing wired (main.tsx wires at startup before the banner can
    // render). Plain reload = the previously shipped behavior.
    io.reload()
    return
  }
  // Armed BEFORE messaging so even a hung registration still ends in a reload.
  // On the normal path the plugin reloads first and this timer dies with the page.
  const fallback = io.setTimeout(() => io.reload(), SW_UPDATE_FALLBACK_MS)
  try {
    await fn()
  } catch {
    io.clearTimeout(fallback)
    io.reload()
  }
}
